import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row, Col, Card } from 'react-bootstrap';
import { FaClipboardList, FaUsers, FaSync, FaTools, FaUserFriends, FaNewspaper, FaGlobeAmericas } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useSyncDrivesMutation, useRebuildDrivesMutation } from '../../slices/drivesApiSlice';
import Loader from '../../components/Loader';


export default function AdminHomeScreen() {
  const { userInfo } = useSelector((state) => state.auth);

  const [syncDrivesApi, { isLoading: isSyncing }] = useSyncDrivesMutation();
  const [rebuildDrivesApi, { isLoading: isRebuilding }] = useRebuildDrivesMutation();

  if (!userInfo) return <Loader />;

  const syncDrivesHandler = async () => {
    try {
      const result = await syncDrivesApi().unwrap();
      toast.success(result.message);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const rebuildDrivesHandler = async () => {
    if (!window.confirm('This will recompute all drive stats (avg speed, benchmark count) from scratch. Manual fields (make, type, description, etc.) will be preserved. Continue?')) return;
    try {
      const result = await rebuildDrivesApi().unwrap();
      toast.success(result.message);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <>
      <Link
        to='/'
        className='btn btn-light'
        style={{ marginTop: '32px', marginBottom: '16px' }}
      >
        Return to Home
      </Link>

      <h1 style={{ marginBottom: '24px' }}>Administrator Home</h1>

      <Row className='g-4'>
        <Col md={6} lg={4}>
          <Card
            as={Link}
            to={`/admin/action_log`}
            className='shadow-sm text-decoration-none h-100'
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className='text-center p-4'>
              <FaClipboardList size={48} className='text-primary mb-3' />
              <Card.Title>Action Log</Card.Title>
              <Card.Text className='text-muted'>
                View a log of administrative actions including user deletions,
                benchmark deletions, and drive deletions.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4}>
          <Card
            as={Link}
            to={`/admin/all_users`}
            className='shadow-sm text-decoration-none h-100'
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className='text-center p-4'>
              <FaUsers size={48} className='text-primary mb-3' />
              <Card.Title>Manage Users</Card.Title>
              <Card.Text className='text-muted'>
                View, edit, add, and delete user accounts.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4}>
          <Card
            as={Link}
            to={`/admin/contributors`}
            className='shadow-sm text-decoration-none h-100'
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className='text-center p-4'>
              <FaUserFriends size={48} className='text-primary mb-3' />
              <Card.Title>Contributors</Card.Title>
              <Card.Text className='text-muted'>
                Browse users who have shared benchmarks, their systems, and
                the benchmarks they've submitted.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4}>
          <Card
            as={Link}
            to={`/admin/submission_stats`}
            className='shadow-sm text-decoration-none h-100'
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className='text-center p-4'>
              <FaGlobeAmericas size={48} className='text-primary mb-3' />
              <Card.Title>Submission Stats</Card.Title>
              <Card.Text className='text-muted'>
                Benchmark uploads over time and the countries and regions
                they're submitted from.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4}>
          <Card
            as={Link}
            to={`/admin/blogs`}
            className='shadow-sm text-decoration-none h-100'
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className='text-center p-4'>
              <FaNewspaper size={48} className='text-primary mb-3' />
              <Card.Title>Manage Blog Posts</Card.Title>
              <Card.Text className='text-muted'>
                View, feature, and manage blog posts from all users.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4}>
          <Card
            className='shadow-sm h-100'
            style={{ cursor: isSyncing ? 'not-allowed' : 'pointer' }}
            onClick={isSyncing ? undefined : syncDrivesHandler}
          >
            <Card.Body className='text-center p-4'>
              <FaSync size={48} className={`text-primary mb-3${isSyncing ? ' fa-spin' : ''}`} />
              <Card.Title>{isSyncing ? 'Syncing Drives…' : 'Sync Drives'}</Card.Title>
              <Card.Text className='text-muted'>
                Incrementally sync any unprocessed benchmarks into the drive
                stats collection.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={4}>
          <Card
            className='shadow-sm h-100'
            style={{ cursor: isRebuilding ? 'not-allowed' : 'pointer' }}
            onClick={isRebuilding ? undefined : rebuildDrivesHandler}
          >
            <Card.Body className='text-center p-4'>
              <FaTools size={48} className='text-primary mb-3' />
              <Card.Title>{isRebuilding ? 'Rebuilding Drives…' : 'Rebuild Drives'}</Card.Title>
              <Card.Text className='text-muted'>
                Recompute all drive stats from scratch. Manual fields are
                preserved.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
